import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Upload, Loader2, CheckCircle2, Clock, XCircle, IdCard, Camera } from "lucide-react";

function UploadBox({ label, icon: Icon, file, onFile, testid }) {
  return (
    <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-congo-blue transition-colors" data-testid={testid}>
      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
      <Icon className={`h-8 w-8 mx-auto ${file ? "text-green-600" : "text-slate-400"}`} />
      <p className="text-sm font-medium mt-2">{file ? file.name : label}</p>
      <p className="text-xs text-slate-400 mt-1">{file ? "Cliquez pour changer" : "JPG, PNG ou PDF"}</p>
    </label>
  );
}

export default function KycPage() {
  const { user, refreshUser } = useAuth();
  const [docType, setDocType] = useState("carte_electeur");
  const [idDoc, setIdDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(user?.kyc_status || "none");

  useEffect(() => { api.get("/kyc/me").then(({ data }) => setStatus(data.status)).catch(() => {}); }, []);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const submit = async () => {
    if (!idDoc || !selfie) { toast.error("Veuillez ajouter votre pièce d'identité et un selfie."); return; }
    setBusy(true);
    try {
      const idPath = await uploadOne(idDoc);
      const selfiePath = await uploadOne(selfie);
      const fd = new FormData();
      fd.append("doc_type", docType); fd.append("id_doc_path", idPath); fd.append("selfie_path", selfiePath);
      await api.post("/kyc/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dossier KYC soumis. Validation sous 24-48h.");
      setStatus("pending"); refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setBusy(false); }
  };

  const StatusBanner = () => {
    const map = {
      approved: { icon: CheckCircle2, c: "bg-green-50 border-green-200 text-green-700", t: "Identité vérifiée", d: "Vous pouvez investir dans tous les projets." },
      pending: { icon: Clock, c: "bg-amber-50 border-amber-200 text-amber-700", t: "En cours de vérification", d: "Votre dossier est en cours d'examen par nos équipes." },
      rejected: { icon: XCircle, c: "bg-red-50 border-red-200 text-red-700", t: "Dossier rejeté", d: "Veuillez soumettre à nouveau vos documents." },
    };
    const s = map[status];
    if (!s) return null;
    return (
      <div className={`rounded-xl border p-4 flex items-start gap-3 ${s.c}`} data-testid="kyc-status-banner">
        <s.icon className="h-6 w-6 shrink-0" />
        <div><p className="font-semibold">{s.t}</p><p className="text-sm opacity-90">{s.d}</p></div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-congo-bluelight flex items-center justify-center"><ShieldCheck className="h-6 w-6 text-congo-blue" /></div>
          <div><h1 className="font-head text-2xl font-bold text-slate-900">Vérification d'identité (KYC)</h1>
            <p className="text-slate-500 text-sm">Obligatoire avant d'investir · conforme BCC anti-blanchiment</p></div>
        </div>

        <div className="mt-6"><StatusBanner /></div>

        {status !== "approved" && status !== "pending" && (
          <div className="mt-6 space-y-5 bg-white border border-slate-200 rounded-2xl p-6">
            <div>
              <Label>Type de document</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="h-12 rounded-lg mt-1" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="carte_electeur">Carte d'électeur</SelectItem>
                  <SelectItem value="carte_identite">Carte d'identité nationale</SelectItem>
                  <SelectItem value="passeport">Passeport</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <UploadBox label="Pièce d'identité" icon={IdCard} file={idDoc} onFile={setIdDoc} testid="upload-id" />
            <UploadBox label="Selfie (contrôle de vivacité)" icon={Camera} file={selfie} onFile={setSelfie} testid="upload-selfie" />
            <Button onClick={submit} disabled={busy} data-testid="kyc-submit-btn" className="w-full h-12 rounded-full bg-congo-blue">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Soumettre mon dossier</>}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
